import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const productHrefs = ['#how-it-works', '#features', '#security', '#pricing']
const useCaseHrefs = ['#use-cases', '#use-cases', '#use-cases', '#use-cases', '#use-cases']

export default function LandingFooter() {
  const { t, i18n } = useTranslation()

  const productLinks = t('landing.landingFooter.productLinks', { returnObjects: true }) as string[]
  const useCaseLinks = t('landing.landingFooter.useCaseLinks', { returnObjects: true }) as string[]

  const legalLinks = t('landing.landingFooter.legalLinks', { returnObjects: true }) as string[]
  const legalItems: { label: string; to: string }[] = [
    { label: legalLinks[0] || t('legal.privacyPolicy'), to: `/${i18n.language}/legal/privacy` },
    { label: legalLinks[1] || t('legal.termsOfUse'), to: `/${i18n.language}/legal/terms` },
    { label: legalLinks[2] || 'Cookie Policy', to: `/${i18n.language}/legal/privacy` },
  ]

  return (
    <>
      {/* CTA Section */}
      <section className="py-20 bg-green-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.cta.headline')}
          </h2>
          <p
            className="text-green-100 text-lg mb-8"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.cta.subheadline')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to={`/${i18n.language}/create`}
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-green-600 font-bold rounded-lg hover:bg-green-50 transition-colors text-base"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('landing.cta.primary')}
            </Link>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-colors text-base"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('landing.cta.secondary')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-1">
              <div
                className="text-white font-bold text-lg mb-3"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <img src="/logo_v3.svg" alt="Sign This!" style={{ height: '1.75rem' }} />
              </div>
              <p
                className="text-sm text-gray-400 leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.landingFooter.tagline')}
              </p>
            </div>

            <div>
              <h4
                className="text-white font-semibold text-sm mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.landingFooter.product')}
              </h4>
              <ul className="space-y-2">
                {productLinks.map((link, idx) => (
                  <li key={link}>
                    <a
                      href={productHrefs[idx] || '#'}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4
                className="text-white font-semibold text-sm mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.landingFooter.useCases')}
              </h4>
              <ul className="space-y-2">
                {useCaseLinks.map((link, idx) => (
                  <li key={link}>
                    <a
                      href={useCaseHrefs[idx] || '#use-cases'}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4
                className="text-white font-semibold text-sm mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.landingFooter.legal')}
              </h4>
              <ul className="space-y-2">
                {legalItems.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p
              className="text-xs text-gray-500"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('landing.landingFooter.copyright')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="mailto:contact@loxia.ai"
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('footer.accessibilityContact')}
              </a>
              <span className="text-xs text-gray-600">·</span>
              <p
                className="text-xs text-gray-500"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.landingFooter.madeWith')}
              </p>
            </div>
          </div>
        </div>
      </footer>

    </>
  )
}
